import React, { useEffect, useState } from 'react';
import ApiService from "../../services/ApiService";
import Loader from "../../components/Loader";
import { formatDate, formatDateLocal } from '../../utils/helper';
import { toast } from 'react-toastify';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const SpecialistClinicReport = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState([]);
    const [specialists, setSpecialists] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [filters, setFilters] = useState({
        start_date: '',
        end_date: '',
        specialist_id: ''
    });

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/reports/specialist-clinic',
                data: filters
            });
            if (response.data.status) {
                setData(response.data.data.appointments);
                setSummary(response.data.data.summary);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch specialist clinic report");
        } finally {
            setLoading(false);
        }
    };

    const fetchSpecialists = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/users?role=specialist&limit=100'
            });
            if (response.data.status) {
                setSpecialists(response.data.data.users);
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchSpecialists();
        fetchReport();
    }, []);

    useEffect(() => {
        fetchReport();
    }, [filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (update) => {
        setDateRange(update);
        const [start, end] = update;
        setFilters(prev => ({
            ...prev,
            start_date: start ? formatDateLocal(start) : "",
            end_date: end ? formatDateLocal(end) : ""
        }));
    };

    const exportToExcel = () => {
        const csvContent = "data:text/csv;charset=utf-8," 
            + "Appointment ID,Date,Specialist,Patient,Checkup Type\n"
            + data.map(e => `${e.id},${formatDate(e.date)},${e.specialist?.name || 'N/A'},${e.patient?.name || 'N/A'},${e.checkup_type?.title || 'N/A'}`).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `specialist_clinic_report_${filters.start_date}_to_${filters.end_date}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Specialist Clinic Report
                        </h1>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card mb-5 mb-xl-10">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1 gap-3">
                                    <div className="d-flex align-items-center position-relative">
                                        <select
                                            name="specialist_id"
                                            className="form-select form-select-solid w-200px"
                                            value={filters.specialist_id}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Specialists</option>
                                            {specialists.map(specialist => (
                                                <option key={specialist.id} value={specialist.id}>{specialist.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="d-flex align-items-center position-relative">
                                        <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateChange}
                                            className="form-control form-control-solid w-250px"
                                            placeholderText="Select Date Range"
                                            dateFormat="yyyy-MM-dd"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex justify-content-end gap-2">
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Export to Excel</Tooltip>}>
                                        <button onClick={exportToExcel} className="btn btn-light-primary">
                                            <i className="fas fa-file-export me-2"></i> Export to Excel
                                        </button>
                                    </OverlayTrigger>
                                </div>
                            </div>
                        </div>

                        <div className="card-body py-4">
                            {loading ? <Loader /> : (
                                <>
                                    <div className="row mb-5">
                                        {summary.map((item, index) => (
                                            <div className="col-md-3" key={index}>
                                                <div className="border border-gray-300 border-dashed rounded py-3 px-4 mb-3">
                                                    <div className="fs-6 fw-bold text-gray-700">{item.specialist?.name || 'Unknown Specialist'}</div>
                                                    <div className="fs-2 fw-bold text-gray-800">{item.total} Visits</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table align-middle table-row-dashed fs-6 gy-5">
                                            <thead>
                                                <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                    <th>Ref No</th>
                                                    <th>Date</th>
                                                    <th>Patient</th>
                                                    <th>Specialist</th>
                                                    <th>Checkup Type</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-gray-600 fw-semibold">
                                                {data.length > 0 ? (
                                                    data.map((appt) => (
                                                        <tr key={appt.id}>
                                                            <td>{appt.ref_no}</td>
                                                            <td>{formatDate(appt.date)}</td>
                                                            <td>{appt.patient?.name}</td>
                                                            <td>{appt.specialist?.name}</td>
                                                            <td>{appt.checkup_type?.name}</td>
                                                            <td>
                                                                <span className={`badge badge-light-${appt.status === 'completed' ? 'success' : 'warning'}`}>
                                                                    {appt.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="text-center">No records found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SpecialistClinicReport;
